import classNames from 'classnames';
import React, {useState} from 'react';

import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import SettingsIcon from 'ui-library/icons/SettingsIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {ElementMouseEvent} from 'util/Types';

export type SettingsIconDropdownItem = OptionData & {
  name: string;
  href?: string;
  onSelect: (e?: ElementMouseEvent) => void;
};

interface Props {
  options: Array<SettingsIconDropdownItem>;
  iconClassName?: string;
  onClick?: () => void;
  dropLeft?: boolean;
  disabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function SettingsIconDropdown({
  options,
  iconClassName,
  onClick,
  dropLeft,
  disabled,
  dataTestAttrs,
}: Props): JSX.Element | null {
  // Make this dropdown controlled so we can do a callback when the dropdown is opened. If the onClick
  // callback is passed to SettingsIcon, Dropdown will assume it is because we want the dropdown to be
  // controlled, and it will use the passed onClick instead of managing its own open state
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dropdown
      isOpen={isOpen}
      menu={
        <Menu dropLeft={dropLeft} height={MenuHeight.MEDIUM} width={MenuWidth.LARGE}>
          {options.map((option) => (
            <Option
              dataTestAttrs={option.dataTestAttrs}
              isDisabled={option.disabled}
              tooltipTitle={option.disabled ? option.disabledMessage : null}
              key={option.name}
              href={option.href}
              onSelect={(_value, e) => {
                option.onSelect(e);
              }}
            >
              {option.name}
            </Option>
          ))}
        </Menu>
      }
      onToggle={(newIsOpen) => {
        setIsOpen(newIsOpen);
      }}
    >
      <SettingsIcon
        className={classNames(iconClassName, 'u-tiny-spacing-left')}
        disabled={disabled}
        dataTestAttrs={dataTestAttrs}
        onClick={() => {
          onClick?.();
          setIsOpen(!isOpen);
        }}
      />
    </Dropdown>
  );
}
